import { IntelligenceLookupType, ISerpApiSearchResult } from "../models/PartLookup";

export interface IIntelligencePriceSummary {
  min: number;
  max: number;
  avg: number;
}

/** Whether the AI result filter took this listing out of the price figures. */
export function isExcluded(result: ISerpApiSearchResult): boolean {
  return !!result.excludedReason && result.excludedReason.trim().length > 0;
}

const LOOKUP_TYPE_LABELS: Record<IntelligenceLookupType, string> = {
  OePartNumber: "OE part number",
  PartNumber: "MPN",
  Vehicle: "make, model and year",
};

const LOOKUP_TYPE_BY_NUMBER: Record<number, IntelligenceLookupType> = {
  0: "OePartNumber",
  1: "PartNumber",
  2: "Vehicle",
};

export function normaliseLookupType(
  value: IntelligenceLookupType | number | string | null | undefined
): IntelligenceLookupType | null {
  if (value == null || value === "") return null;
  if (typeof value === "number") return LOOKUP_TYPE_BY_NUMBER[value] ?? null;
  if (value === "OePartNumber" || value === "PartNumber" || value === "Vehicle") return value;
  const asNumber = Number(value);
  if (!Number.isNaN(asNumber)) return LOOKUP_TYPE_BY_NUMBER[asNumber] ?? null;
  return null;
}

export function lookupTypeLabel(type: IntelligenceLookupType | number | string | null | undefined): string | null {
  const normalised = normaliseLookupType(type);
  if (!normalised) return null;
  return LOOKUP_TYPE_LABELS[normalised] ?? null;
}

/**
 * What the lookup matched on and the term it searched, e.g. OE part number "1K0615301AA". Every
 * distinct search (MPN, make/model, OE) is run; when more than one hit, the labels are joined and
 * the term is left off. Null when the results carry no lookup type.
 */
export function describeMatch(
  results: ISerpApiSearchResult[] | null | undefined,
): { label: string; term: string | null } | null {
  const types = [
    ...new Set(
      (results ?? [])
        .map((r) => normaliseLookupType(r.matchedOn))
        .filter((type): type is IntelligenceLookupType => type != null)
    ),
  ];
  if (types.length === 0) return null;

  if (types.length === 1) {
    const label = lookupTypeLabel(types[0]);
    if (!label) return null;
    const term = results?.find((r) => normaliseLookupType(r.matchedOn) === types[0])?.searchTerm?.trim();
    return { label, term: term ? term : null };
  }

  const labels = types.map(lookupTypeLabel).filter((label): label is string => !!label);
  if (labels.length === 0) return null;
  return { label: labels.join(" and "), term: null };
}

/**
 * Min, max and mean asking price of the eBay listings Wilma Intelligence returned, rounded to the
 * penny. Listings without a positive price, and listings the AI result filter excluded, are ignored;
 * null when none are left. Matches the API's IntelligencePriceSummary, which stores the same figures
 * on the part.
 */
export function summarisePrices(
  results: ISerpApiSearchResult[] | null | undefined,
): IIntelligencePriceSummary | null {
  const prices = (results ?? [])
    .filter((r) => !isExcluded(r))
    .map((r) => r.priceExtracted)
    .filter((p): p is number => p != null && p > 0);

  if (prices.length === 0) return null;

  const round = (n: number) => Math.round(n * 100) / 100;

  return {
    min: round(Math.min(...prices)),
    max: round(Math.max(...prices)),
    avg: round(prices.reduce((sum, p) => sum + p, 0) / prices.length),
  };
}
